import dayjs, { type Dayjs } from "dayjs";
import { formatDate, formatRelativeUnits } from "@marekl/adminer/format";
import type {
  Competition,
  CompetitionStatus,
  CompetitionType,
  EntryPhase,
} from "./queries/competitions";
import type { EntryStatus } from "./queries/entries";

// Canonical Czech labels for competition enums, shared by every surface
// that renders them (admin list, competition mode, club pages) so the
// wording never diverges — see the terminology table in AGENTS.md.

type BadgeVariant = "ok" | "warn" | "err" | "info" | "muted";

export const COMPETITION_STATUS_LABELS: Record<CompetitionStatus, string> = {
  draft: "Připravuje se",
  announced: "Ohlášeno",
  published: "Zveřejněno",
  running: "Probíhá",
  finished: "Dokončeno",
  archived: "Archivováno",
};

// Colours follow docs/mockups/competitions.html: „Probíhá" is amber, not
// green — a competition running right now wants attention, it is not a
// state that has come out well. „Dokončeno" is the green one, which is
// also what separates it from „Archivováno" at a glance.
export const COMPETITION_STATUS_VARIANT: Record<CompetitionStatus, BadgeVariant> = {
  draft: "muted",
  announced: "muted",
  published: "info",
  running: "warn",
  finished: "ok",
  archived: "muted",
};

export const COMPETITION_TYPE_LABELS: Record<CompetitionType, string> = {
  mcr_qualification: "Kvalifikační kolo MČR",
  mcr_final: "Finále MČR",
  cup: "Pohárovka",
};

export const ENTRY_PHASE_LABELS: Record<EntryPhase, string> = {
  not_open: "Přihlašování neotevřeno",
  open: "Přihlašování",
  closed_deadline: "Přihlášky uzavřeny",
  closed_capacity: "Kapacita naplněna",
};

export const ENTRY_PHASE_VARIANT: Record<EntryPhase, BadgeVariant> = {
  not_open: "muted",
  open: "ok",
  closed_deadline: "warn",
  closed_capacity: "warn",
};

/**
 * The one badge a club's surfaces put beside a competition, or null.
 *
 * Nothing while přihlašování is open: „Přihlašování" on every open card
 * read as an instruction nobody could follow, and the uzávěrky beside it
 * already say how long it stays open. A running or finished competition
 * says so; otherwise only a closed phase is worth flagging.
 */
export function clubCompetitionBadge(
  c: Pick<Competition, "status" | "entry_phase">,
): { label: string; variant: BadgeVariant } | null {
  if (c.status === "running" || c.status === "finished" || c.status === "archived") {
    return { label: COMPETITION_STATUS_LABELS[c.status], variant: COMPETITION_STATUS_VARIANT[c.status] };
  }
  if (c.entry_phase === "open") return null;
  return { label: ENTRY_PHASE_LABELS[c.entry_phase], variant: ENTRY_PHASE_VARIANT[c.entry_phase] };
}

/** One date, or the „13. 6. 2026 – 14. 6. 2026" range of a multi-day
 * competition. */
export function formatCompetitionDates(c: Pick<Competition, "date" | "date_to">): string {
  if (!c.date_to || c.date_to === c.date) return formatDate(c.date);
  return `${formatDate(c.date)}–${formatDate(c.date_to)}`;
}

/** The same, without the year, for a column where every date shares it (the
 * season track): „7. 3.", „7.–8. 3.", or „30. 4.–1. 5." across a month. */
export function formatCompetitionDatesShort(c: Pick<Competition, "date" | "date_to">): string {
  const from = dayjs(c.date);
  if (!from.isValid()) return c.date;
  const to = c.date_to ? dayjs(c.date_to) : null;
  if (!to || !to.isValid() || to.isSame(from, "day")) return from.format("D. M.");
  if (to.isSame(from, "month")) return `${from.format("D.")}–${to.format("D. M.")}`;
  return `${from.format("D. M.")}–${to.format("D. M.")}`;
}

export const ENTRY_STATUS_LABELS: Record<EntryStatus, string> = {
  submitted: "Podáno",
  withdrawn: "Odhlášeno",
};

export const ENTRY_STATUS_VARIANT: Record<EntryStatus, BadgeVariant> = {
  submitted: "ok",
  withdrawn: "muted",
};

/**
 * Czech countdown to a deadline in calendar days: „dnes", „zítra",
 * „pozítří", then „za 3 dny" / „za 5 dní"; null once the deadline passed.
 *
 * Compares startOf("day") values — a deadline tomorrow morning is „zítra"
 * and not „dnes", which a raw hour-truncating diff would claim. That guard
 * and the passed-deadline null are the only decisions left here: the
 * wording is Intl's, via `formatRelativeUnits`.
 *
 * `numeric: "auto"` is a deliberate choice against the library default.
 * „always" would print „za 1 den" / „za 2 dny", which scans more evenly in
 * a list; the words read better in the one place that matters most, the
 * deadline a club is about to miss. Czech has idiomatic words for 0, 1 and
 * 2 only, so the register switches to numbers from three days out on its
 * own. Note that `formatCalendar` owns the same „zítra" register with a
 * time attached — nothing here calls it today, but a surface showing both
 * would read „zítra v 10:00 · zítra".
 */
export function daysLeftLabel(deadline: Dayjs, now: Dayjs = dayjs()): string | null {
  const days = deadline.startOf("day").diff(now.startOf("day"), "day");
  if (days < 0) return null;
  return formatRelativeUnits(days, "day", { numeric: "auto" });
}

/**
 * How close an uzávěrka has to be before a countdown is worth printing.
 * „(za 186 dní)" next to every competition is noise, and noise next to the
 * one deadline that *is* near is what makes that one easy to miss.
 */
export const COUNTDOWN_FROM_DAYS = 30;

/**
 * `daysLeftLabel`, but only once the deadline is news: null when it is
 * further off than `withinDays`, and null once it has passed.
 */
export function countdownIfNear(
  deadline: Dayjs,
  now: Dayjs = dayjs(),
  withinDays: number = COUNTDOWN_FROM_DAYS,
): string | null {
  const days = deadline.startOf("day").diff(now.startOf("day"), "day");
  if (days < 0 || days > withinDays) return null;
  return daysLeftLabel(deadline, now);
}

/**
 * Dokončeno and archived freeze a competition whole (server: writegate,
 * R20): settings, přihlášky, start list, categories, offer, music. Pages
 * hide their edit controls on it instead of offering what the server
 * refuses with 409.
 */
export function isFrozen(c: Pick<Competition, "status"> | undefined): boolean {
  return c?.status === "finished" || c?.status === "archived";
}

/**
 * What this screen may do with a competition's live domain (panels,
 * devices, and in Probíhá the start list and přihlášky, R25): `edit`
 * before the competition, `live` while it runs here (each change is a live
 * command), `elsewhere` while the hall runs it (read-only here: the server
 * refuses with lease_elsewhere), `frozen` once it is over. One answer for
 * every page, so they lock alike.
 */
export type LiveAccess = "edit" | "live" | "elsewhere" | "frozen";

export function liveAccess(
  c: Pick<Competition, "status" | "run_elsewhere"> | undefined,
): LiveAccess {
  if (isFrozen(c)) return "frozen";
  if (c?.run_elsewhere) return "elsewhere";
  return c?.status === "running" ? "live" : "edit";
}

/** Whether a page may offer changes under that access. */
export function mayChangeLive(access: LiveAccess): boolean {
  return access === "edit" || access === "live";
}

/** The sentence a page shows while it may not change anything: frozen, or
 * run by the hall. Null while it may. */
export function liveAccessNotice(
  c: Pick<Competition, "status" | "run_elsewhere"> | undefined,
): string | null {
  const access = liveAccess(c);
  if (access === "frozen") return frozenNotice(c);
  if (access === "elsewhere")
    return "Soutěž teď řídí hala. Tady je vše jen pro čtení; měnit se dá na místě.";
  return null;
}

/** The sentence a frozen competition's pages show; the server's words. */
export function frozenNotice(c: Pick<Competition, "status"> | undefined): string | null {
  if (c?.status === "finished") return "Soutěž je dokončená a už se nemění.";
  if (c?.status === "archived") return "Soutěž je archivovaná a je pouze pro čtení.";
  return null;
}
